import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Button,
    Input,
    Modal,
    message,
    Space,
    Card,
    Image,
    Popconfirm,
    Tag,
    Tooltip,
    Upload,
    Select,
    InputNumber,
    Form,
    Row,
    Col,
    Divider
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    UploadOutlined,
    DownloadOutlined,
    ImportOutlined,
    ExportOutlined,
    MedicineBoxOutlined,
    PictureOutlined
} from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import { getuserdata } from '../../services/apiService';
import ImageShow from '../../Uitiles/ImageShow';
import { OneKlicksFileSDK } from '../../SDK/OneKlicksFileSDK';
import { useAuth } from '../../context/AuthContext';
import '../../assets/styles/dashboard.css';
import './PharmacyProducts.css';

const { Option } = Select;

// Pharmacy Product Model Class
class PharmacyProductModel {
    constructor(data = {}) {
        this.id = data.id || null;
        this.product_name = data.product_name || '';
        this.description = data.description || '';
        this.category = data.category || '';
        this.brand = data.brand || '';
        this.price = data.price || 0;
        this.image = data.image || '';
        this.status = data.status || 'active';
        this.prescription_required = data.prescription_required || false;
        this.created_at = data.created_at || new Date().toISOString();
        this.updated_at = data.updated_at || new Date().toISOString();
    }

    toJSON() {
        return {
            product_name: this.product_name,
            description: this.description,
            category: this.category,
            brand: this.brand,
            price: this.price,
            image: this.image,
            status: this.status,
            prescription_required: this.prescription_required,
            created_at: this.created_at,
            updated_at: this.updated_at
        };
    }
}

const PharmacyProducts = () => {
    const { userData } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);

    const fetchProducts = useCallback(async () => {
        if (!userData || !userData.id) return;

        try {
            setLoading(true);
            const tableSchema = '';
            const tableName = 'pharmacy_products';
            const conditions = { pharmacy_id: userData.id };
            const orderBy = 'id ASC';
            const search = searchText?.trim() ? {
                product_name: searchText.trim(),
                brand: searchText.trim(),
                category: searchText.trim()
            } : {};
            const paginationData = {
                page: pagination.current,
                limit: pagination.pageSize
            };

            console.log('Fetching pharmacy products with params:', { tableName, conditions, orderBy, search, paginationData });

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, paginationData);

            console.log('API Response:', response);

            if (response.success) {
                const productData = response.data || [];
                console.log('Product data:', productData);
                setProducts(productData);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || productData.length
                }));
            } else {
                console.log('API returned error or empty:', response);
                // Don't show error for empty, just set empty
                if (response.message === "No data found") {
                    setProducts([]);
                    setPagination(prev => ({ ...prev, total: 0 }));
                } else {
                    message.error('Failed to fetch products');
                }
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            message.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, searchText, userData]);

    const fetchCategories = useCallback(async () => {
        try {
            console.log('Fetching pharmacy categories...');
            const tableSchema = '';
            const tableName = 'pharmacy_categories';
            const conditions = {};
            const orderBy = 'name ASC';
            const search = {};
            const paginationData = {
                page: 1,
                limit: 100
            };

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, paginationData);

            console.log('Categories API Response:', response);

            if (response.success) {
                const categoryData = response.data || [];
                console.log('Categories fetched:', categoryData);
                setCategories(categoryData);
            } else {
                console.error('Failed to fetch categories:', response);
                message.error('Failed to fetch categories');
                // Set default categories if table doesn't exist
                setCategories([
                    { id: 1, name: 'Antibiotics' },
                    { id: 2, name: 'Pain Relief' },
                    { id: 3, name: 'Vitamins & Supplements' },
                    { id: 4, name: 'First Aid' },
                    { id: 5, name: 'Personal Care' },
                    { id: 6, name: 'Baby Care' },
                    { id: 7, name: 'Women Health' },
                    { id: 8, name: 'Men Health' },
                    { id: 9, name: 'Diabetes Care' },
                    { id: 10, name: 'Heart Health' },
                    { id: 11, name: 'Other' }
                ]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            message.error('Failed to fetch categories');
            // Set default categories on error
            setCategories([
                { id: 1, name: 'Antibiotics' },
                { id: 2, name: 'Pain Relief' },
                { id: 3, name: 'Vitamins & Supplements' },
                { id: 4, name: 'First Aid' },
                { id: 5, name: 'Personal Care' },
                { id: 6, name: 'Baby Care' },
                { id: 7, name: 'Women Health' },
                { id: 8, name: 'Men Health' },
                { id: 9, name: 'Diabetes Care' },
                { id: 10, name: 'Heart Health' },
                { id: 11, name: 'Other' }
            ]);
        }
    }, []);

    const fetchBrands = useCallback(async () => {
        try {
            console.log('Fetching pharmacy brands...');
            const tableSchema = '';
            const tableName = 'pharmacy_brands';
            const conditions = {};
            const orderBy = 'id ASC';
            const search = {};
            const paginationData = {
                page: 1,
                limit: 100
            };

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, paginationData);

            console.log('Brands API Response:', response);

            if (response.success) {
                const brandData = response.data || [];
                console.log('Brands fetched:', brandData);
                setBrands(brandData);
            } else {
                console.error('Failed to fetch brands:', response);
                message.error('Failed to fetch brands');
                // Set default brands if table doesn't exist
                setBrands([
                    { id: 1, name: 'Pfizer' },
                    { id: 2, name: 'Johnson & Johnson' },
                    { id: 3, name: 'Novartis' },
                    { id: 4, name: 'Roche' },
                    { id: 5, name: 'Merck' },
                    { id: 6, name: 'GlaxoSmithKline' },
                    { id: 7, name: 'AstraZeneca' },
                    { id: 8, name: 'Bayer' },
                    { id: 9, name: 'Sanofi' },
                    { id: 10, name: 'Other' }
                ]);
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
            message.error('Failed to fetch brands');
            // Set default brands on error
            setBrands([
                { id: 1, name: 'Pfizer' },
                { id: 2, name: 'Johnson & Johnson' },
                { id: 3, name: 'Novartis' },
                { id: 4, name: 'Roche' },
                { id: 5, name: 'Merck' },
                { id: 6, name: 'GlaxoSmithKline' },
                { id: 7, name: 'AstraZeneca' },
                { id: 8, name: 'Bayer' },
                { id: 9, name: 'Sanofi' },
                { id: 10, name: 'Other' }
            ]);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchBrands();
    }, [fetchProducts, fetchCategories, fetchBrands]);

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Debounced search to improve performance
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    handleSearch(value);
                }, 300);
            };
        })(),
        []
    );

    const handleTableChange = (paginationInfo) => {
        setPagination(prev => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize
        }));
    };

    const showModal = () => {
        setShowAddModal(true);
        form.resetFields();
    };

    const closeModal = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        setEditData(null);
        form.resetFields();
    };

    const handleShowEditModal = (record) => {
        setEditData(record);
        setShowEditModal(true);

        // Prepare image for form if it exists
        let imageValue = undefined;
        if (record.image) {
            imageValue = {
                fileList: [{
                    uid: '-1',
                    name: 'image.jpg',
                    status: 'done',
                    url: record.image
                }]
            };
        }

        form.setFieldsValue({
            product_name: record.product_name,
            description: record.description,
            category: record.category,
            brand: record.brand,
            price: record.price,
            status: record.status,
            prescription_required: record.prescription_required,
            image: imageValue
        });
    };

    const handleDelete = async (id) => {
        try {
            const tableSchema = '';
            const tableName = 'pharmacy_products';
            const conditions = { id: id };

            const response = await oneKlicks.delete(tableSchema, tableName, conditions);

            if (response.success) {
                message.success('Product deleted successfully');
                fetchProducts();
            } else {
                message.error(response.message || 'Failed to delete product');
            }
        } catch (error) {
            console.error('Error deleting product:', error);
            message.error('Failed to delete product');
        }
    };

    const handleSubmit = async (values) => {
        try {
            setSubmitLoading(true);
            const tableSchema = '';
            const tableName = 'pharmacy_products';

            // Check for duplicate product names (excluding current record if editing)
            const existingProduct = products.find(product =>
                product.product_name.toLowerCase() === values.product_name.toLowerCase() &&
                (!editData || product.id !== editData.id)
            );

            if (existingProduct) {
                message.error('A product with this name already exists');
                return;
            }

            // Validate required fields
            if (!values.product_name || !values.category || !values.brand) {
                message.error('Product name, category, and brand are required');
                return;
            }

            // Validate price
            if (values.price < 0) {
                message.error('Price cannot be negative');
                return;
            }

            // Handle image upload if there's a file
            if (values.image && values.image.fileList && values.image.fileList.length > 0) {
                const file = values.image.fileList[0].originFileObj;
                const uploadResponse = await OneKlicksFileSDK.uploadFile(file);
                if (uploadResponse.success) {
                    values.image = uploadResponse.fileId;
                } else {
                    message.error('Failed to upload image');
                    return;
                }
            } else if (values.image && typeof values.image === 'string') {
                // Keep existing image if it's already a string (file ID)
                values.image = values.image;
            } else {
                values.image = '';
            }

            if (showEditModal && editData) {
                // Update existing product
                const conditions = { id: editData.id };
                const response = await oneKlicks.update(tableSchema, tableName, conditions, values);

                if (response.success) {
                    message.success('Product updated successfully');
                    closeModal();
                    fetchProducts();
                } else {
                    message.error(response.message || 'Failed to update product');
                }
            } else {
                // Create new product
                values.pharmacy_id = userData.id;
                const response = await oneKlicks.create(tableSchema, tableName, values);

                if (response.success) {
                    message.success('Product created successfully');
                    closeModal();
                    fetchProducts();
                } else {
                    message.error(response.message || 'Failed to create product');
                }
            }
        } catch (error) {
            console.error('Error saving product:', error);
            message.error('Failed to save product');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleImportCSV = (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const csv = e.target.result;
                const lines = csv.split('\n');
                const headers = lines[0].split(',');

                const products = [];
                for (let i = 1; i < lines.length; i++) {
                    if (lines[i].trim()) {
                        const values = lines[i].split(',');
                        const product = {};
                        headers.forEach((header, index) => {
                            product[header.trim()] = values[index]?.trim() || '';
                        });
                        products.push(product);
                    }
                }

                if (products.length === 0) {
                    message.error('No valid products found in CSV file');
                    return;
                }

                // Import products to database
                const tableSchema = '';
                const tableName = 'pharmacy_products';
                let successCount = 0;
                let errorCount = 0;

                for (const product of products) {
                    try {
                        product.pharmacy_id = userData.id;
                        const response = await oneKlicks.create(tableSchema, tableName, product);
                        if (response.success) {
                            successCount++;
                        } else {
                            errorCount++;
                            console.error('Failed to import product:', product, response);
                        }
                    } catch (error) {
                        errorCount++;
                        console.error('Error importing product:', product, error);
                    }
                }

                if (successCount > 0) {
                    message.success(`Successfully imported ${successCount} products${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
                    fetchProducts();
                } else {
                    message.error('Failed to import any products');
                }
            } catch (error) {
                console.error('Error importing CSV:', error);
                message.error('Failed to import CSV file');
            }
        };
        reader.readAsText(file);
        return false; // Prevent default upload behavior
    };

    const handleExportCSV = () => {
        const headers = ['product_name', 'description', 'category', 'brand', 'price', 'status', 'prescription_required'];
        const csvContent = [
            headers.join(','),
            ...products.map(product =>
                headers.map(header => {
                    const value = product[header];
                    return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pharmacy_products.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        message.success('Products exported successfully');
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            render: (id) => <span className="text-muted">#{id}</span>
        },
        {
            title: 'Product',
            dataIndex: 'product_name',
            key: 'product_name',
            width: 200,
            render: (name, record) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: 50, height: 50, marginRight: 12 }}>
                        {record.image ? (
                            <div style={{ width: 50, height: 50, borderRadius: '4px', overflow: 'hidden' }}>
                                <ImageShow image={record.image} width={50} height={50} />
                            </div>
                        ) : (
                            <div style={{
                                width: 50,
                                height: 50,
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px'
                            }}>
                                <MedicineBoxOutlined style={{ color: '#999', fontSize: '20px' }} />
                            </div>
                        )}
                    </div>
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.brand}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            render: (category) => (
                <Tag color="blue">{category}</Tag>
            )
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: 100,
            render: (price) => (
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                    {parseFloat(price).toFixed(2)}
                </span>
            )
        },
        {
            title: 'Prescription',
            dataIndex: 'prescription_required',
            key: 'prescription_required',
            width: 120,
            render: (required) => (
                <Tag color={required ? 'red' : 'green'}>
                    {required ? 'Required' : 'Not Required'}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                Modal.info({
                                    title: record.product_name,
                                    content: (
                                        <div>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <strong>Brand:</strong> {record.brand}
                                                    </div>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <strong>Category:</strong> {record.category}
                                                    </div>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <strong>Price:</strong> {parseFloat(record.price).toFixed(2)}
                                                    </div>
                                                </Col>
                                                <Col span={12}>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <strong>Prescription Required:</strong> {record.prescription_required ? 'Yes' : 'No'}
                                                    </div>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <strong>Status:</strong> {record.status}
                                                    </div>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <strong>Description:</strong>
                                                    </div>
                                                    <div style={{
                                                        padding: 8,
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: 4,
                                                        fontSize: '12px'
                                                    }}>
                                                        {record.description || 'No description available'}
                                                    </div>
                                                </Col>
                                            </Row>
                                            {record.image && (
                                                <div style={{ marginTop: 16, textAlign: 'center' }}>
                                                    <div style={{ width: 200, height: 150, borderRadius: '8px', overflow: 'hidden', margin: '0 auto' }}>
                                                        <ImageShow image={record.image} width={200} height={150} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ),
                                    width: 600
                                });
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleShowEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Are you sure you want to delete this product?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <div className="">
                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
                    <Card className="shadow-sm" style={{ width: '100%' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Input
                                placeholder="Search products by name, brand, or category"
                                prefix={<SearchOutlined />}
                                style={{ width: 350 }}
                                onChange={(e) => debouncedSearch(e.target.value)}
                                allowClear
                            />
                            <Space>
                                <Upload
                                    accept=".csv"
                                    showUploadList={false}
                                    beforeUpload={handleImportCSV}
                                >
                                    <Button icon={<ImportOutlined />}>
                                        Import CSV
                                    </Button>
                                </Upload>
                                <Button
                                    icon={<ExportOutlined />}
                                    onClick={handleExportCSV}
                                >
                                    Export CSV
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={showModal}
                                >
                                    Add Product
                                </Button>
                            </Space>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={products}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} products`
                            }}
                            onChange={handleTableChange}
                            tableLayout="fixed"
                            style={{ width: '100%' }}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: '40px 0', textAlign: 'center' }}>
                                        <div style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}>
                                            💊
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#666', marginBottom: '8px' }}>
                                            No products found
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#999' }}>
                                            {products.length === 0 ?
                                                'Get started by adding your first pharmacy product' :
                                                'Try adjusting your search criteria'
                                            }
                                        </div>
                                        {products.length === 0 && (
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={showModal}
                                                style={{ marginTop: '16px' }}
                                            >
                                                Add First Product
                                            </Button>
                                        )}
                                    </div>
                                )
                            }}
                        />
                    </Card>
                </div>
            </div>

            {/* Add/Edit Product Modal */}
            <Modal
                title={showEditModal ? 'Edit Product' : 'Add New Product'}
                open={showAddModal || showEditModal}
                onCancel={closeModal}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        status: 'active',
                        prescription_required: false
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="product_name"
                                label="Product Name"
                                rules={[{ required: true, message: 'Please enter product name' }]}
                            >
                                <Input placeholder="Enter product name" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="brand"
                                label="Brand"
                                rules={[{ required: true, message: 'Please select brand' }]}
                            >
                                <Select placeholder="Select brand">
                                    {brands.map(brand => (
                                        <Option key={brand.id} value={brand.name}>{brand.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: 'Please select category' }]}
                            >
                                <Select placeholder="Select category">
                                    {categories.map(category => (
                                        <Option key={category.id} value={category.name}>{category.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="price"
                                label="Price"
                                rules={[{ required: true, message: 'Please enter price' }]}
                            >
                                <InputNumber
                                    placeholder="0.00"
                                    min={0}
                                    step={0.01}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{ required: true, message: 'Please select status' }]}
                            >
                                <Select placeholder="Select status">
                                    <Option value="active">Active</Option>
                                    <Option value="inactive">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="prescription_required"
                        label="Prescription Required"
                    >
                        <Select placeholder="Select prescription requirement">
                            <Option value={true}>Yes</Option>
                            <Option value={false}>No</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="image"
                        label="Product Image"
                    >
                        <Upload
                            listType="picture-card"
                            maxCount={1}
                            beforeUpload={() => false}
                            accept="image/*"
                            showUploadList={{
                                showPreviewIcon: true,
                                showRemoveIcon: true,
                            }}
                        >
                            <div>
                                <PictureOutlined />
                                <div style={{ marginTop: 8 }}>Upload Image</div>
                            </div>
                        </Upload>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input.TextArea
                            placeholder="Enter product description"
                            rows={4}
                        />
                    </Form.Item>

                    <Divider />

                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={submitLoading} disabled={submitLoading}>
                                {showEditModal ? 'Update Product' : 'Add Product'}
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </section>
    );
};

export default PharmacyProducts; 
